export interface Character {
  charId: string;
  name: string;
  icon: { set: string; id: number; hue: number; body?: number; hat?: number; face?: number; accentHue?: number; border?: string; background?: string; mask?: string };
  createdAt: string;
  totalPoints: number;
  /** Phase A — season-lantern buildout. Defaults to 0 for legacy rows. */
  embers?: number;
  /** Phase A — season-lantern buildout. Defaults to 0 for legacy rows. */
  lanterns?: number;
  favoriteGameId?: string | null;
  selectedTitle?: string | null;
  seatCosmetics?: SeatCosmetics;
  /** True when a PIN is set; client uses this to choose set-PIN vs verify-PIN flow. */
  hasPin?: boolean;
}

export class ApiError extends Error {
  /** Parsed response body when the server returned JSON. Useful for
   *  pulling structured fields (e.g. retryAfterSec on a `locked` error)
   *  without having to re-shape the error site-by-site. */
  public body: unknown;
  constructor(status: number, code: string, body?: unknown) {
    super(`api ${status} ${code}`);
    this.status = status;
    this.code = code;
    this.body = body ?? null;
  }
  public status: number;
  public code: string;
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  });
  const text = await res.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!res.ok) {
    const code = (body as { error?: string })?.error ?? `http-${res.status}`;
    throw new ApiError(res.status, code, body);
  }
  return body as T;
}

export async function gate(code: string): Promise<{ ok: true; deviceId: string }> {
  return req('/api/auth/gate', { method: 'POST', body: JSON.stringify({ code }) });
}

export async function whoami(): Promise<{ ok: boolean; deviceId: string; charId: string | null; kv: string; embers: number; lanterns: number }> {
  return req('/api/whoami');
}

export async function getCharacter(charId: string): Promise<Character> {
  return req(`/api/characters/${encodeURIComponent(charId)}`);
}

export async function listCharacters(cursor?: string): Promise<{ items: Character[]; nextCursor?: string }> {
  const q = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  return req(`/api/characters${q}`);
}

export async function createCharacter(name: string, icon: Character['icon'] | unknown): Promise<Character> {
  return req('/api/characters', { method: 'POST', body: JSON.stringify({ name, icon }) });
}

export async function updateCharacter(charId: string, patch: { icon?: Character['icon']; favoriteGameId?: string | null; selectedTitle?: string | null; seatCosmetics?: SeatCosmetics }): Promise<Character> {
  return req(`/api/characters/${encodeURIComponent(charId)}/update`, { method: 'POST', body: JSON.stringify(patch) });
}

export async function claimCharacter(charId: string): Promise<Character> {
  return req(`/api/characters/${encodeURIComponent(charId)}/claim`, { method: 'POST' });
}

export async function releaseCharacter(): Promise<{ ok: true }> {
  return req('/api/characters/_/release', { method: 'POST' });
}

export async function searchCharacters(q: string): Promise<{ items: Character[] }> {
  return req(`/api/characters/search?q=${encodeURIComponent(q)}`);
}

export async function setCharacterPin(charId: string, pin: string): Promise<{ ok: true; hasPin: true }> {
  return req(`/api/characters/${encodeURIComponent(charId)}/pin/set`, {
    method: 'POST',
    body: JSON.stringify({ pin }),
  });
}

export async function verifyCharacterPin(charId: string, pin: string): Promise<Character> {
  return req(`/api/characters/${encodeURIComponent(charId)}/pin/verify`, {
    method: 'POST',
    body: JSON.stringify({ pin }),
  });
}

export async function forgetThisDevice(charId: string): Promise<{ ok: true }> {
  return req(`/api/characters/${encodeURIComponent(charId)}/forget-device`, { method: 'POST' });
}

export interface LobbyTicket {
  roomCode: string;
  gameId: string;
  wsUrl: string;
  ticket: string;
}

export async function createLobby(opts: { gameId?: string; settings?: Record<string, unknown>; visibility?: 'public' | 'private' } = {}): Promise<LobbyTicket> {
  return req('/api/lobbies', { method: 'POST', body: JSON.stringify(opts) });
}

export interface PublicLobbyRow {
  roomCode: string;
  gameId: string;
  hostName: string;
  hostIcon: { set: string; id: number; hue: number; body?: number; hat?: number; face?: number; accentHue?: number };
  liveSeats: number;
  createdAt: string;
}

export async function listPublicLobbies(cursor?: string): Promise<{ items: PublicLobbyRow[]; nextCursor?: string }> {
  const q = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  return req(`/api/lobbies/public${q}`);
}


export interface InputBindings {
  primary: { label: string; key?: string[] };
  alt?: Array<{ label: string; key?: string[] }>;
}
export interface GameTile {
  id: string;
  baseGameId?: string;
  formatId?: 'ffa' | 'duel' | 'team-2v2' | 'one-v-three' | 'coop' | null;
  name: string;
  pitch: string;
  category: string;
  chaoticness: number;
  durationSec: number;
  controls: string[];
  recommendedFor: string[];
  minPlayers?: number;
  maxPlayers?: number;
  playerFormats?: Array<{
    id: 'ffa' | 'duel' | 'team-2v2' | 'one-v-three' | 'coop';
    label: string;
    minPlayers: 2 | 3 | 4;
    maxPlayers: 2 | 3 | 4;
    countMode: 'exact' | 'range';
  }>;
  tutorial?: { headline: string; tip: string };
  displayMode?: 'canvas' | 'dom';
  inputBindings?: InputBindings;
}

export async function listGames(): Promise<{ games: GameTile[] }> {
  return req('/api/games');
}

export interface LeaderboardEntry {
  rank: number;
  charId: string;
  name: string;
  icon: { set: string; id: number; hue: number };
  bestScore: number;
}

export async function getLeaderboard(gameId: string, limit = 20): Promise<{ gameId: string; name: string; entries: LeaderboardEntry[] }> {
  return req(`/api/games/${encodeURIComponent(gameId)}/leaderboard?limit=${limit}`);
}

export interface MasteryRow {
  gameId: string;
  gameName: string;
  tier: 0 | 1 | 2 | 3;
  played: number;
  wins: number;
  nextLabel: string | null;
  nextHint: string | null;
}

export interface CharacterStats {
  charId: string;
  name: string;
  icon: Character['icon'];
  totalPoints: number;
  accountXp?: number;
  accountLevel?: number;
  accountXpToNext?: { current: number; needed: number };
  embers?: number;
  lanterns?: number;
  season?: {
    season: number;
    seasonXp: number;
    tier: number;
    toNext: { current: number; needed: number };
    claimed: boolean[];
    rewards: Array<{ tier: number; free?: { kind: 'cosmetic' | 'embers' | 'lantern' | 'title' | 'boost'; ref: string | number; label: string; rarity: 1 | 2 | 3 | 4 | 5 } }>;
  };
  daily?: {
    date: string;
    challenges: Array<{
      id: string;
      label: string;
      target: number;
      progress: number;
      completed: boolean;
      rewardSeasonXp: number;
      rewardEmbers: number;
    }>;
  };
  favoriteGameId?: string | null;
  selectedTitle?: string | null;
  seatCosmetics?: SeatCosmetics;
  matchesPlayed: number;
  roundWins: number;
  setWins: number;
  mvpAwards: number;
  dailyStreak?: number;
  bestDailyStreak?: number;
  lastVisitDate?: string | null;
  perGame: Record<string, { bestScore: number; totalScore: number; matchesPlayed: number; wins: number; roundWins: number; mvpAwards: number; lastPlayedAt: string | null }>;
  achievements: Array<{ code: string; label: string; earnedAt: string | null; gameId: string | null }>;
  inventory: CosmeticInventory;
}

export async function getCharacterStats(charId: string): Promise<CharacterStats> {
  return req(`/api/characters/${encodeURIComponent(charId)}/stats`);
}

export interface ActivityRow {
  ts: number;
  tier: 1 | 2 | 3;
  kind: string;
  title: string;
  subtitle?: string | null;
  rewardChip?: string | null;
  rarity: 1 | 2 | 3 | 4;
  icon?: string | null;
}

export async function getCharacterActivity(charId: string, limit = 50): Promise<{ items: ActivityRow[] }> {
  return req(`/api/characters/${encodeURIComponent(charId)}/activity?limit=${limit}`);
}

export async function shopBuy(charId: string, itemId: string): Promise<{ ok: true; embersSpent: number; item: { id: string; label: string } }> {
  return req(`/api/characters/${encodeURIComponent(charId)}/shop/buy`, {
    method: 'POST',
    body: JSON.stringify({ itemId }),
  });
}

export interface CosmeticInventory {
  bodies: number[];
  hats: number[];
  faces: number[];
  hues: number[];
  accentHues: number[];
  titles: string[];
  seatBorders?: string[];
  seatBackgrounds?: string[];
  seatParticles?: string[];
  seatBgHues?: number[];
  emotes?: string[];
  /** New 3-color palette ids (Phase 2). Replaces hue+accentHue when set
   *  on the equipped icon. */
  palettes?: string[];
  /** Phase E1/F3 — avatar-frame cosmetic ids (semantic catalog ids,
   *  e.g. 'leaf-ring', 'ember-ring'). Optional; the server defaults to
   *  []. */
  borders?: string[];
  /** Phase E1/F3 — avatar-backdrop cosmetic ids (semantic catalog ids,
   *  e.g. 'tavern-hearth', 'forges-maw'). Optional; the server defaults
   *  to []. */
  backgrounds?: string[];
  /** Phase F5 — face-overlay mask ids (semantic, e.g. 'domino',
   *  'crimson-veil'). Optional; the server defaults to []. */
  masks?: string[];
  unlockHints?: Array<{
    kind: 'body' | 'hat' | 'face' | 'hue' | 'accentHue' | 'title' | 'emote' | 'seatBorder' | 'seatBackground' | 'seatParticle' | 'seatBgHue' | 'palette' | 'border' | 'background' | 'mask';
    id: number | string;
    label: string;
    hint: string;
    unlocked: boolean;
    progress?: { current: number; target: number; label: string };
  }>;
  mastery?: MasteryRow[];
}

export interface SeatCosmetics {
  border: string;
  background: string;
  particle: string;
  /** Optional hue 0–359 for the seat-tile gradient. null = use the
   *  preset background. Mirrors @tt/lobby/presence SeatCosmetics. */
  bgHue?: number | null;
}

export async function joinLobby(code: string): Promise<LobbyTicket> {
  const c = code.trim().toUpperCase();
  return req(`/api/lobbies/${encodeURIComponent(c)}/join`, { method: 'POST' });
}

// ── Season pass ────────────────────────────────────────────────────────────
// Mirrors the lambdas/season response shape. Reward arrays are flat
// {tier, kind, ref, label, rarity} — see shared/lobby/src/season.ts.
// Claimed arrays carry literal 1-indexed tier numbers (tier 1 = value 1).

export interface SeasonRewardWire {
  tier: number;
  kind: 'cosmetic' | 'embers' | 'lantern' | 'title' | 'boost';
  ref: string | number;
  label: string;
  rarity: 1 | 2 | 3 | 4 | 5;
}

export interface SeasonState {
  season: number;
  /** Player's current tier number (0..60). A tier-N reward is claimable
   *  when `tier >= N`. */
  tier: number;
  current: number;
  needed: number;
  seasonXp: number;
  claimedFree: number[];
  claimedPremium: number[];
  hasPremium: boolean;
  lanterns: number;
  embers: number;
  rewardsFree: SeasonRewardWire[];
  rewardsPremium: SeasonRewardWire[];
}

export interface SeasonClaimResult {
  ok: true;
  alreadyClaimed?: boolean;
  granted: { kind: SeasonRewardWire['kind']; ref: string | number; label: string; rarity: 1 | 2 | 3 | 4 | 5 };
  lanterns?: number;
  embers?: number;
}

export async function getSeasonState(): Promise<SeasonState> {
  return req('/api/season/state');
}

export async function claimSeasonTier(tier: number, track: 'free' | 'premium'): Promise<SeasonClaimResult> {
  return req('/api/season/claim', {
    method: 'POST',
    body: JSON.stringify({ tier, track }),
  });
}

export interface BuyPremiumWithLanternsResult {
  ok: true;
  hasPremium: true;
  lanterns: number;
}

/** Phase D — spend 1000 lanterns to unlock this season's premium pass.
 *  Server-side atomic; throws ApiError 402 `insufficient-lanterns`,
 *  409 `already-premium`, or 400 `wrong-season` on the predictable
 *  failure paths. */
export async function buyPremiumWithLanterns(season: number): Promise<BuyPremiumWithLanternsResult> {
  return req('/api/season/buy-premium-with-lanterns', {
    method: 'POST',
    body: JSON.stringify({ season }),
  });
}

// ── Lantern shop ───────────────────────────────────────────────────────────
// Mirrors lambdas/shop/src/index.ts. Cosmetic / Bundle defs are echoed
// directly from `shared/lobby/src/cosmetics-catalog.ts` — we re-import the
// types here to avoid forcing every UI consumer to depend on the shared
// package's re-export style.

export type CosmeticSourceWire =
  | { kind: 'default' }
  | { kind: 'level'; level: number }
  | { kind: 'achievement'; id: string }
  | { kind: 'season-free'; season: number; tier: number }
  | { kind: 'season-premium'; season: number; tier: number }
  | { kind: 'lantern-shop'; price: number }
  | { kind: 'embers-shop'; price: number }
  | { kind: 'bundle'; bundleId: string };

export type CosmeticKindWire =
  | 'body' | 'hat' | 'face' | 'accent' | 'palette'
  | 'border' | 'background' | 'particles' | 'title';

export interface CosmeticDefWire {
  id: string;
  kind: CosmeticKindWire;
  name: string;
  rarity: 1 | 2 | 3 | 4 | 5;
  source: CosmeticSourceWire;
  tags?: string[];
  season?: number;
}

export interface BundleDefWire {
  id: string;
  name: string;
  contents: string[];
  price: number;
  rarity: 1 | 2 | 3 | 4 | 5;
}

export interface ShopState {
  lanterns: number;
  featured: CosmeticDefWire[];
  catalog: CosmeticDefWire[];
  bundles: BundleDefWire[];
  /** Flat list of every cosmetic id the player already owns (across all
   *  inventory buckets, stringified). */
  owned: string[];
}

export interface PurchaseResult {
  ok: true;
  lanterns: number;
  granted: string[];
}

export async function getShopState(): Promise<ShopState> {
  return req('/api/shop/state');
}

export async function purchaseShopItem(
  itemId: string,
  type: 'cosmetic' | 'bundle',
): Promise<PurchaseResult> {
  return req('/api/shop/purchase', {
    method: 'POST',
    body: JSON.stringify({ itemId, type }),
  });
}

// ── Stripe payments ────────────────────────────────────────────────────────
// Phase C3 of the season-lantern buildout. The server-side handler lives in
// lambdas/payments — it validates the SKU, creates a Stripe Checkout
// Session, and returns the Stripe-hosted URL the client redirects to.
//
// Webhook completion flips the player's lanterns/premium server-side; the
// client never grants currency directly. After a successful checkout the
// browser returns to /payment-success?cs={CHECKOUT_SESSION_ID}.

export interface CheckoutSession {
  checkoutUrl: string;
}

export async function createCheckoutSession(sku: string): Promise<CheckoutSession> {
  return req('/api/payments/checkout', {
    method: 'POST',
    body: JSON.stringify({ sku }),
  });
}

export interface PaymentHistoryItem {
  sessionId: string;
  sku: string;
  cents: number;
  ts: number;
  fulfilled: boolean;
}

export async function getPaymentHistory(): Promise<{ items: PaymentHistoryItem[] }> {
  return req('/api/payments/history');
}
